import React from 'react';
import { motion } from 'framer-motion';
import { Shield, UserCheck, FileText, AlertTriangle, Phone, Mail, MapPin, ExternalLink, ClipboardList, Scale, BookOpen, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { contactInfo } from '../config/contactInfo';

const POPIACompliance: React.FC = () => {
  const conditions = [
    { name: 'Accountability', desc: 'BioAxis takes responsibility for complying with POPIA and can demonstrate compliance.' },
    { name: 'Processing Limitation', desc: 'We only process personal information that is adequate, relevant, and not excessive for the stated purpose.' },
    { name: 'Purpose Specification', desc: 'We collect information for specific, explicitly defined, and lawful purposes.' },
    { name: 'Further Processing Limitation', desc: 'We do not process personal information for purposes incompatible with the original collection purpose.' },
    { name: 'Information Quality', desc: 'We take reasonable steps to ensure personal information is complete, accurate, and up to date.' },
    { name: 'Openness', desc: 'We are transparent about how we process personal information (this page and our Privacy Policy).' },
    { name: 'Security Safeguards', desc: 'We implement appropriate technical and organisational measures to protect personal information.' },
    { name: 'Data Subject Participation', desc: 'We enable data subjects to exercise their rights to access, correct, and delete their personal information.' },
  ];

  const requestSteps = [
    { step: '1', title: 'Submit Your Request', desc: 'Send an email to our Information Officer with your request, including your full name and sufficient details to identify your account or records.' },
    { step: '2', title: 'Identity Verification', desc: 'We will verify your identity to protect against unauthorised access to personal information. This may require providing a copy of your ID document.' },
    { step: '3', title: 'Request Processing', desc: 'We will process your request within 30 days as required by POPIA. Complex requests may take longer, and we will inform you of any delays.' },
    { step: '4', title: 'Response', desc: 'You will receive a written response confirming the action taken, or explaining any reasons for refusal (where legally permitted).' },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Shield className="w-16 h-16 text-primary-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              POPIA <span className="gradient-text">Compliance</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              BioAxis is committed to full compliance with the Protection of Personal Information Act 4 of 2013 (POPIA). This page provides details about our compliance measures and how to exercise your data protection rights.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: February 2026
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Information Officer Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mb-12"
          >
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <UserCheck className="w-8 h-8 text-primary-400" />
              Information Officer (POPIA Section 55)
            </h2>
            <p className="text-gray-300 leading-relaxed mb-6">
              In terms of POPIA, BioAxis has appointed an Information Officer who is responsible for encouraging compliance with the conditions for lawful processing, dealing with data subject requests, and working with the Information Regulator.
            </p>
            <div className="bg-primary-900/20 border border-primary-700/30 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-primary-300 mb-4">Information Officer Details</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-300">
                  <Mail className="w-5 h-5 text-primary-400 flex-shrink-0" />
                  <span>
                    <strong>Email:</strong>{' '}
                    <a href={`mailto:${contactInfo.email.privacy}`} className="text-primary-400 hover:text-primary-300 underline">
                      {contactInfo.email.privacy}
                    </a>
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <Phone className="w-5 h-5 text-primary-400 flex-shrink-0" />
                  <span><strong>Phone:</strong> {contactInfo.phone.display}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <MapPin className="w-5 h-5 text-primary-400 flex-shrink-0" />
                  <span><strong>Address:</strong> {contactInfo.business.address.full}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <Clock className="w-5 h-5 text-primary-400 flex-shrink-0" />
                  <span><strong>Response time:</strong> Within 30 days of receiving a valid request</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* 8 Conditions */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mb-12"
          >
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <ClipboardList className="w-8 h-8 text-primary-400" />
              The 8 Conditions for Lawful Processing
            </h2>
            <p className="text-gray-300 mb-6">
              POPIA sets out 8 conditions that must be met for the lawful processing of personal information. BioAxis adheres to all 8 conditions:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {conditions.map((condition, index) => (
                <motion.div
                  key={condition.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-dark-800/50 border border-dark-700/50 rounded-xl p-4"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    <div>
                      <h4 className="font-semibold text-white">{condition.name}</h4>
                      <p className="text-sm text-gray-400 mt-1">{condition.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Your Rights */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mb-12"
          >
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <Scale className="w-8 h-8 text-primary-400" />
              Your Data Subject Rights
            </h2>
            <p className="text-gray-300 mb-6">
              Under POPIA, you have the following rights in relation to your personal information:
            </p>
            <ul className="space-y-4">
              {[
                { right: 'Right of access (Section 23)', detail: 'Request confirmation of whether we hold your personal information and request a copy of it.' },
                { right: 'Right to correction (Section 24)', detail: 'Request correction or deletion of personal information that is inaccurate, irrelevant, excessive, out of date, incomplete, misleading, or obtained unlawfully.' },
                { right: 'Right to object (Section 11(3))', detail: 'Object to the processing of your personal information on reasonable grounds relating to your particular situation.' },
                { right: 'Right to withdraw consent', detail: 'Where processing is based on your consent, you may withdraw that consent at any time. Withdrawal does not affect the lawfulness of processing before withdrawal.' },
                { right: 'Right to deletion', detail: 'Request deletion of your personal information where it is no longer necessary for the purpose for which it was collected, subject to legal retention requirements.' },
                { right: 'Right not to be subject to automated decision-making (Section 71)', detail: 'You have the right not to be subject to a decision based solely on automated processing that significantly affects you.' },
                { right: 'Right to lodge a complaint (Section 74)', detail: 'Lodge a complaint with the Information Regulator if you believe your rights under POPIA have been infringed.' },
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <strong className="text-white">{item.right}:</strong>{' '}
                    <span>{item.detail}</span>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* How to Exercise Rights */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mb-12"
          >
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <FileText className="w-8 h-8 text-primary-400" />
              How to Submit a Data Subject Request
            </h2>
            <p className="text-gray-300 mb-6">
              To exercise any of your rights under POPIA, follow these steps:
            </p>
            <div className="space-y-6">
              {requestSteps.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-500/20 border border-primary-500/30 flex items-center justify-center">
                    <span className="text-primary-400 font-bold">{step.step}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white text-lg">{step.title}</h4>
                    <p className="text-gray-400 mt-1">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="bg-dark-800/50 border border-dark-700/50 rounded-xl p-4 mt-6">
              <p className="text-gray-400 text-sm">
                <strong className="text-gray-300">Note:</strong> BioAxis may charge a reasonable fee for manifestly unfounded or excessive requests, as permitted by POPIA Section 23(2). We will inform you of any applicable fee before processing the request.
              </p>
            </div>
          </motion.div>

          {/* Our Role as Operator */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mb-12"
          >
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-primary-400" />
              BioAxis as Responsible Party & Operator
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>
                <strong className="text-white">As Responsible Party:</strong> When we collect your personal information directly (e.g., account registration, contact forms, billing), BioAxis is the Responsible Party and determines the purpose and means of processing.
              </p>
              <p>
                <strong className="text-white">As Operator:</strong> When healthcare professionals use our platforms (VetAxis, StaffAxis, NoteAxis) to process patient or staff data, BioAxis acts as an Operator (processor) under POPIA Section 20-21. The healthcare professional or practice is the Responsible Party for that data.
              </p>
              <p>
                In our role as Operator, we:
              </p>
              <ul className="space-y-2 ml-4">
                {[
                  'Process personal information only on the documented instructions of the Responsible Party',
                  'Implement appropriate security measures to protect the personal information',
                  'Do not engage sub-processors without prior authorisation',
                  'Assist the Responsible Party in responding to data subject requests',
                  'Delete or return all personal information upon termination of the service agreement',
                  'Make available all information necessary to demonstrate compliance with POPIA',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Information Regulator */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mb-12"
          >
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-yellow-400" />
              Information Regulator (South Africa)
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>
                If you are not satisfied with how BioAxis has handled your personal information or responded to your request, you have the right to lodge a complaint with the Information Regulator of South Africa.
              </p>
              <div className="bg-dark-800/50 border border-dark-700/50 rounded-xl p-6 space-y-3">
                <h4 className="font-semibold text-white">Information Regulator Contact Details</h4>
                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary-400" />
                    <strong>General enquiries:</strong>&nbsp;inforeg@justice.gov.za
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary-400" />
                    <strong>POPIA complaints:</strong>&nbsp;POPIAComplaints@inforegulator.org.za
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary-400" />
                    <strong>Phone:</strong>&nbsp;012 406 4818
                  </p>
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary-400" />
                    <strong>Address:</strong>&nbsp;JD House, 27 Stiemens Street, Braamfontein, Johannesburg, 2001
                  </p>
                  <p className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-primary-400" />
                    <strong>Website:</strong>&nbsp;www.justice.gov.za/inforeg
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-400">
                We encourage you to contact our Information Officer first to resolve any concerns before approaching the Information Regulator.
              </p>
            </div>
          </motion.div>

          {/* Related Policies */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl"
          >
            <h3 className="text-2xl font-bold mb-6">Related Policies</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { name: 'Privacy Policy', href: '/privacy', desc: 'Full details on how we collect and process personal information' },
                { name: 'Cookie Policy', href: '/cookies', desc: 'How we use cookies and tracking technologies' },
                { name: 'Terms of Service', href: '/terms', desc: 'Terms governing use of our services' },
                { name: 'Acceptable Use Policy', href: '/acceptable-use', desc: 'Rules for using our platforms' },
              ].map((policy) => (
                <Link
                  key={policy.href}
                  to={policy.href}
                  className="block p-4 rounded-xl bg-dark-800/50 hover:bg-dark-700/50 border border-dark-700/50 hover:border-primary-500/30 transition-all duration-200"
                >
                  <p className="font-semibold text-primary-400">{policy.name}</p>
                  <p className="text-sm text-gray-400 mt-1">{policy.desc}</p>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-12 text-center"
          >
            <h3 className="text-2xl font-bold mb-4">Contact Our Information Officer</h3>
            <p className="text-gray-300 mb-6">
              For any POPIA-related queries, data subject requests, or complaints, please reach out to our Information Officer.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href={`mailto:${contactInfo.email.privacy}`}
                whileHover={{ scale: 1.05 }}
                className="btn-primary"
              >
                Email Information Officer
              </motion.a>
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                className="btn-outline"
              >
                Contact Us
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default POPIACompliance;
